import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import { LineBasicNodeMaterial, MeshStandardNodeMaterial } from 'three/webgpu';
import { MODELS_BASE } from '@/hero/preload';
import { net } from '@/lib/net';

const BASE = MODELS_BASE + 'site-innsbruck/';
const PLACEHOLDERS = 40;

export interface SiteMeshes {
  source: 'osm' | 'placeholder';
  buildings: THREE.BatchedMesh;
  streets: THREE.LineSegments | null;
  origin: { lat: number; lon: number } | null;
}

interface StreetsFile {
  origin: { lat: number; lon: number };
  streets: { points: [number, number][] }[];
}

let loading: Promise<SiteMeshes> | null = null;

// The pipeline's OSM neighbours when they were baked, a seeded ring of blocks otherwise.
// Loaded once; the scene mounts the same meshes on every visit.
export function loadNeighbours(heroRadius: number): Promise<SiteMeshes> {
  loading ??= Promise.all([fetchGlb(BASE + 'neighbours.glb'), fetchStreets(BASE + 'streets.json')])
    .then(([scene, streets]): SiteMeshes => {
      if (!scene) return { source: 'placeholder', ...placeholders(heroRadius) };
      return {
        source: 'osm',
        buildings: batchOf(scene),
        streets: streets && streetLines(streets),
        origin: streets?.origin ?? null,
      };
    })
    .catch((err: unknown) => {
      console.warn('[world] neighbours failed, placing blocks instead:', err);
      return { source: 'placeholder', ...placeholders(heroRadius) };
    });
  return loading;
}

function buildingMaterial(): MeshStandardNodeMaterial {
  const m = new MeshStandardNodeMaterial();
  m.color.set('#5a606b');
  m.roughness = 1;
  m.metalness = 0;
  return m;
}

// A missing file comes back as the SPA's index.html with status 200, so check the magic.
async function fetchGlb(url: string): Promise<THREE.Object3D | null> {
  const res = await net(url).catch(() => null);
  if (!res?.ok) return null;
  const bytes = await res.arrayBuffer();
  const magic = new TextDecoder().decode(new Uint8Array(bytes, 0, Math.min(4, bytes.byteLength)));
  if (magic !== 'glTF') return null;
  const loader = new GLTFLoader().setMeshoptDecoder(MeshoptDecoder);
  return (await loader.parseAsync(bytes, '')).scene;
}

async function fetchStreets(url: string): Promise<StreetsFile | null> {
  const res = await net(url).catch(() => null);
  if (!res?.ok) return null;
  try {
    const json = (await res.json()) as StreetsFile;
    return Array.isArray(json.streets) ? json : null;
  } catch {
    return null;
  }
}

// Quantized glTF attributes come in as normalized integers; the batch wants one float layout.
function floatGeometry(src: THREE.BufferGeometry): THREE.BufferGeometry {
  const out = new THREE.BufferGeometry();
  for (const name of ['position', 'normal']) {
    const attr = src.getAttribute(name);
    if (!attr) continue;
    const array = new Float32Array(attr.count * 3);
    for (let i = 0; i < attr.count; i++)
      array.set([attr.getX(i), attr.getY(i), attr.getZ(i)], i * 3);
    out.setAttribute(name, new THREE.BufferAttribute(array, 3));
  }
  if (!out.getAttribute('normal')) out.computeVertexNormals();
  const index = src.getIndex();
  const count = index ? index.count : src.getAttribute('position').count;
  const indices = new Uint32Array(count);
  for (let i = 0; i < count; i++) indices[i] = index ? index.getX(i) : i;
  out.setIndex(new THREE.BufferAttribute(indices, 1));
  return out;
}

function batchOf(scene: THREE.Object3D): THREE.BatchedMesh {
  scene.updateMatrixWorld(true);
  const parts: { geometry: THREE.BufferGeometry; matrix: THREE.Matrix4 }[] = [];
  scene.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (mesh.isMesh)
      parts.push({ geometry: floatGeometry(mesh.geometry), matrix: mesh.matrixWorld });
  });
  const vertices = parts.reduce((n, p) => n + p.geometry.getAttribute('position').count, 0);
  const indices = parts.reduce((n, p) => n + p.geometry.getIndex()!.count, 0);
  const batch = new THREE.BatchedMesh(parts.length, vertices, indices, buildingMaterial());
  for (const p of parts)
    batch.setMatrixAt(batch.addInstance(batch.addGeometry(p.geometry)), p.matrix);
  return shadowed(batch);
}

function streetLines(file: StreetsFile): THREE.LineSegments {
  const positions: number[] = [];
  for (const { points } of file.streets)
    for (let i = 1; i < points.length; i++) {
      const [ax, az] = points[i - 1]!;
      const [bx, bz] = points[i]!;
      positions.push(ax, 0.03, az, bx, 0.03, bz);
    }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  const material = new LineBasicNodeMaterial();
  material.color.set('#3d4a5c');
  const lines = new THREE.LineSegments(geometry, material);
  lines.raycast = () => undefined;
  return lines;
}

// mulberry32: the same ring on every load
function seeded(seed: number): () => number {
  let a = seed;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// 40 blocks, 6 to 18 m tall, 20 to 160 m from the hero's centre, clear of its footprint and of
// the street-level camera.
function placeholders(heroRadius: number): Pick<SiteMeshes, 'buildings' | 'streets' | 'origin'> {
  const random = seeded(10);
  const box = new THREE.BoxGeometry(1, 1, 1).translate(0, 0.5, 0);
  const batch = new THREE.BatchedMesh(PLACEHOLDERS, 24, 36, buildingMaterial());
  const geometry = batch.addGeometry(box);
  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const up = new THREE.Vector3(0, 1, 0);
  for (let i = 0; i < PLACEHOLDERS; i++) {
    const width = 8 + random() * 14;
    const depth = 8 + random() * 12;
    const height = 6 + random() * 12;
    const near = Math.max(20, heroRadius + Math.hypot(width, depth) / 2 + 20);
    const distance = near + (160 - near) * Math.sqrt(random());
    const angle = i * 2.399963 + random() * 0.6;
    q.setFromAxisAngle(up, Math.round(random() * 3) * (Math.PI / 12));
    m.compose(
      new THREE.Vector3(Math.cos(angle) * distance, 0, Math.sin(angle) * distance),
      q,
      new THREE.Vector3(width, height, depth),
    );
    batch.setMatrixAt(batch.addInstance(geometry), m);
  }
  return { buildings: shadowed(batch), streets: null, origin: null };
}

function shadowed(batch: THREE.BatchedMesh): THREE.BatchedMesh {
  batch.castShadow = true;
  batch.receiveShadow = true;
  batch.raycast = () => undefined;
  return batch;
}
